import { Button, Row, Table } from 'antd'
import DeleteButton from '../common/DeleteButton'
import ItemQuantity from '../Items/ItemQuantity'
import { ViewItem, createViewItems } from './templateModel'
import { hasValue } from '../../helpers/nullable'
import ProductionRateForm from '../ItemCatalog/ProductionRateForm'
import { ProductionRate } from '../../domain/productionRate'
import { useContext, useState } from 'react'
import OverrideStringValueModal from '../OverrideModals/OverrideStringValueModal'
import CustomizableValue from '../Items/CustomizableValue'
import TemplateContext from './TemplateContext'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DraggableSortableHeader, DraggableSortableRow, NonDraggableSortableRow } from './SortableRow'
import { CatalogItemDndContext } from '../ItemCatalog/CatalogBrowserDndContext'
import { styleDeletedRows } from '../Items/styleDeletedRows'
import { UndeleteButton } from '../common/UndeleteButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons'

type ProductionRateFormState = {
  isOpen: boolean,
  templateItemId: number,
  productionRate?: Partial<ProductionRate>
}

const defaultProductionRateFormState: ProductionRateFormState = {
  isOpen: false,
  templateItemId: 0,
  productionRate: undefined
}

const StyledTable = styleDeletedRows<typeof Table<ViewItem>>(Table)

const TemplateItems = () => {
  const {state, dispatch, serverTemplate, isTemplateDeleted} = useContext(TemplateContext)
  const {draggingViewItem} = useContext(CatalogItemDndContext)
  const viewItems: ViewItem[] = createViewItems(state, serverTemplate.items, isTemplateDeleted)
  const [productionRateFormState, setProductionRateFormState] = useState<ProductionRateFormState>(defaultProductionRateFormState)
  const [overrideNameModalState, setOverrideNameModalState] = useState<{isOpen: boolean, item?: ViewItem }>({isOpen: false, item: undefined})

  const viewItemsWithDraggingItem = draggingViewItem ? [...viewItems, draggingViewItem] : viewItems

  const editProductionRate = (item: ViewItem) => {
    setProductionRateFormState({
      isOpen: true,
      templateItemId: item.id,
      productionRate: {
        rateInstallationQuantity: item.rateInstallationQuantity,
        ratePerUnits: item.ratePerUnits,
        rateUsageQuantity: item.rateUsageQuantity,
        units: item.units
      }
    })
  }

  const hasProductionRateData = (item: ViewItem): item is Required<ViewItem>  => {
    return hasValue(item.rateInstallationQuantity) && hasValue(item.ratePerUnits) && hasValue(item.rateUsageQuantity)
  }

  const closeProductionRateForm = () => {
    setProductionRateFormState(defaultProductionRateFormState)
  }

  const closeOverrideNameModal = () => {
    setOverrideNameModalState({isOpen: false})
  }

  return <SortableContext items={viewItemsWithDraggingItem.map(x => x.dragId)} strategy={verticalListSortingStrategy}>
    <StyledTable
      dataSource={viewItemsWithDraggingItem}
      rowKey="dragId"
      components={{
        header: {
          row:  DraggableSortableHeader,
        },
        body: {
          row: isTemplateDeleted ? NonDraggableSortableRow : DraggableSortableRow
        }
      }}
      rowClassName={(item: ViewItem) => item.isDeleted ? 'deleted' : ''}
      columns={[
        {
          title: 'Name',
          render: (_, item) => {
            if (item.isEmptyRow) {
              return <></>
            }
            return <CustomizableValue 
              canCustomize={!item.isEntireRowDisabled}
              isCustomized={item.name !== item.catalogName}
              item={item}
              openOverrideDialog={() => setOverrideNameModalState({isOpen: true, item})}
            >
              {item.name}
            </CustomizableValue>
          }
        },
        {
          title: 'Production Rate',
          render: (_, item) => {
            if (item.isEmptyRow) {
              return <></>
            }
            let display: string | undefined
            if (hasProductionRateData(item)) {
              display = `${item.rateInstallationQuantity} ${item.ratePerUnits} per ${item.rateUsageQuantity} ${item.units}`
            }

            return <Row justify="space-between" align="middle" style={{flexDirection: 'row-reverse', gap: 8}}>
              <Button disabled={item.isEntireRowDisabled} icon={<FontAwesomeIcon icon={faPenToSquare} />} onClick={() => editProductionRate(item)} />
              {display}
            </Row>
          }
        },
        {
          title: 'Default Quantity',
          render: (_, item) => {
            if (item.isEmptyRow) {
              return <></>
            }
            return <ItemQuantity 
              item={{ 
                ...item,
                rateCalculationData: hasProductionRateData(item) ? {
                  name: item.name,
                  rateInstallationQuantity: item.rateInstallationQuantity,
                  ratePerUnits: item.ratePerUnits,
                  rateUsageQuantity: item.rateUsageQuantity,
                  units: item.units
                } : undefined
              }}
              onChange={value => dispatch({type: 'ITEM_QUANTITY_UPDATED', templateItemId: item.id, value, serverTemplate})}
              showRateCalculator={hasProductionRateData(item)} />
          }
        },
        {
          align: 'right',
          render: (_, item) => {
            if (item.isEmptyRow || isTemplateDeleted) {
              return <></>
            }
            return item.isDeleted 
              ? <UndeleteButton onClick={() => dispatch({ type: 'ITEM_UNDELETED', templateItemId: item.id })} />
              : <DeleteButton onClick={() => dispatch({ type: 'ITEM_DELETED', templateItemId: item.id })} /> 
          }
        }
      ]}
      pagination={{hideOnSinglePage: true, pageSize: 100}}
    />
    <ProductionRateForm 
      isOpen={productionRateFormState.isOpen}
      productionRate={productionRateFormState.productionRate}
      onChange={(values) => {
        dispatch({ type: 'PRODUCTION_RATE_UPDATED', templateItemId: productionRateFormState.templateItemId, serverTemplate, productionRate: values })
        closeProductionRateForm()
      }}
      onCancel={() => closeProductionRateForm()}
      onClear={() => { 
        dispatch({ type: 'PRODUCTION_RATE_CLEARED', templateItemId: productionRateFormState.templateItemId, serverTemplate })
        closeProductionRateForm()
      }}
    />
    <OverrideStringValueModal 
      visible={overrideNameModalState.isOpen}
      defaultValue={overrideNameModalState.item?.catalogName ?? ''}
      currentValue={overrideNameModalState.item?.name ?? ''}
      label="catalog name"
      name="this item"
      onCancel={() => closeOverrideNameModal()}
      onReset={() => {
        dispatch({type: 'ITEM_NAME_RESET', templateItemId: overrideNameModalState.item!.id, serverTemplate})
        closeOverrideNameModal()
      }}
      onSubmit={(value) => {
        dispatch({type: 'ITEM_NAME_UPDATED', templateItemId: overrideNameModalState.item!.id, serverTemplate, value})
        closeOverrideNameModal()
      }}
      maxLength={150}
    />
  </SortableContext>
}

export default TemplateItems